import { useState, useEffect, FormEvent } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/data';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9so9ttlbrt1qcgaj6t0';

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '蜜桃视频',
  url: 'https://mitao-video.com',
  description: '专注甜蜜生活方式的创意短视频平台，汇聚美食、旅行、时尚、萌宠等多元内容。',
  foundingDate: '2024-03',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: aboutInfo.contact.phone,
    contactType: 'customer service',
    availableLanguage: 'Chinese',
    email: aboutInfo.contact.email,
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: '北京',
    addressRegion: '朝阳区',
    streetAddress: '望京SOHO T1 12层',
  },
  sameAs: [
    'https://weibo.com/mitaovideo',
    'https://weixin.qq.com/mitaovideo',
  ],
};

const aboutPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于蜜桃视频',
  description: '蜜桃视频品牌故事、发展历程、用户口碑和联系方式',
  url: 'https://mitao-video.com/about',
};

function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const hp = formData.get('phone_alt');
    if (hp && typeof hp === 'string' && hp.trim() !== '') {
      setStatus('success');
      setStatusMsg('消息已发送，我们会尽快回复您！');
      return;
    }
    formData.delete('phone_alt');

    setStatus('loading');
    setStatusMsg('');

    try {
      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed as Record<string, string>)?.message || '';

      if (res.ok && (parsed as Record<string, string>)?.code === 'OK') {
        setStatus('success');
        setStatusMsg('消息已发送，我们会尽快回复您！');
        form.reset();
      } else {
        setStatus('error');
        setStatusMsg(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setStatus('error');
      setStatusMsg('网络错误，请检查连接后重试');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
      <div>
        <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
        <input
          id="contact_name"
          name="name"
          type="text"
          required
          className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
        <input
          id="contact_email"
          name="email"
          type="email"
          required
          className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          placeholder="请输入您的邮箱"
        />
      </div>
      <div>
        <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
        <input
          id="contact_subject"
          name="subject"
          type="text"
          required
          className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          placeholder="请输入消息主题"
        />
      </div>
      <div>
        <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息</label>
        <textarea
          id="contact_message"
          name="message"
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
          placeholder="请输入您的消息（最多500字）"
        ></textarea>
      </div>

      <input
        type="text"
        name="phone_alt"
        className="form-hp-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
      >
        {status === 'loading' ? '发送中...' : '发送消息'}
      </button>

      {statusMsg && (
        <p className={`text-sm text-center ${status === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
          {statusMsg}
        </p>
      )}
    </form>
  );
}

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们 - 品牌故事与联系方式"
        description="了解蜜桃视频的品牌故事、发展历程与用户口碑。蜜桃视频是专注甜蜜生活方式的创意短视频平台，汇聚美食、旅行、时尚、萌宠等多元内容。查看联系方式，加入我们。"
        keywords="蜜桃视频,关于蜜桃视频,蜜桃视频品牌介绍,蜜桃视频联系,蜜桃视频口碑,蜜桃视频发展历程"
        canonicalUrl="https://mitao-video.com/about"
        schemaData={[orgSchema, aboutPageSchema]}
      />

      <div className="pt-20">
        {/* Page Header */}
        <div className="relative h-[240px] md:h-[320px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=soft%20warm%20peach%20colored%20abstract%20gradient%20background%20with%20gentle%20flowing%20shapes%2C%20minimalist%20elegant%20atmosphere%2C%20cream%20and%20coral%20warm%20tones%2C%20artistic%20abstract%20composition%2C%20soft%20dreamy%20texture&width=1800&height=600&seq=about-header-01&orientation=landscape"
            alt="关于蜜桃视频"
            title="蜜桃视频 - 关于我们"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/35"></div>
          <div className="absolute inset-0 flex items-center justify-center text-center">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold font-heading text-background-50 mb-3">
                关于我们
              </h1>
              <p className="text-background-50/75 text-base md:text-lg">
                了解蜜桃视频背后的故事
              </p>
            </div>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Brand Story */}
        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
                品牌故事
              </span>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                一颗蜜桃的甜蜜旅程
              </h2>
            </div>
            <div className="bg-background-100 rounded-2xl p-8 md:p-10">
              {aboutInfo.brandStory.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="text-foreground-700 leading-relaxed mb-4 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">
                发展历程
              </span>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                每一步成长，都有你见证
              </h2>
            </div>
            <div className="space-y-0">
              {aboutInfo.milestones.map((milestone, idx) => (
                <div key={idx} className="flex gap-5">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-primary-500 mt-1.5 shrink-0"></div>
                    {idx < aboutInfo.milestones.length - 1 && (
                      <div className="w-0.5 flex-1 bg-primary-200 mt-1"></div>
                    )}
                  </div>
                  <div className="pb-10">
                    <span className="text-xs text-primary-600 font-medium">{milestone.year}</span>
                    <h3 className="text-lg font-semibold text-foreground-900 mt-1 mb-1">{milestone.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-3">
                用户口碑
              </span>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                听听他们怎么说
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutInfo.testimonials.map((testimonial, idx) => (
                <div key={idx} className="bg-background-100 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-11 h-11 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{testimonial.name}</p>
                      <p className="text-xs text-foreground-500">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{testimonial.content}</p>
                  <div className="flex items-center gap-0.5 mt-4">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-secondary-500 text-sm"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
                联系我们
              </span>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                期待与你相遇
              </h2>
              <p className="text-foreground-600 text-base">
                有任何问题或合作意向，欢迎随时联系我们
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-background-50 rounded-2xl p-8">
                <h3 className="text-lg font-semibold text-foreground-900 mb-6">联系方式</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电话</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-wechat-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微信</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-weibo-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微博</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.weibo}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-50 rounded-2xl p-8">
                <h3 className="text-lg font-semibold text-foreground-900 mb-6">发送消息</h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}